/*
 * Sticky add to cart bar (mobile). Markup: sections/zv-sticky-atc.liquid.
 *
 * The bar never owns the variant. Its two lists are read from the main buy
 * box DOM every time they are opened and every time the main form changes,
 * choosing a value clicks the matching main option, and the button submits
 * the main product form. The main picker therefore keeps control of the
 * variant, the URL, the price, stock and Camouflage-hidden values, and one
 * tap gives exactly one /cart/add.js request.
 */

const MOBILE = window.matchMedia('(max-width: 767px)');
const VISIBLE_CLASS = 'zv-sticky-atc--visible';
const OPEN_CLASS = 'zv-sticky-atc__dropdown--open';
const PAGE_CLASS = 'zv-sticky-atc-open';

// Selectors inside the main buy box (component_ProductForm.liquid and
// component_ProductButtonOption.liquid).
const MAIN = {
    component: '.product-details product-form',
    form: 'form.product-form__form',
    submit: 'button[data-ref="submitButton"]',
    status: '[data-ref="buttonStatus"]',
    price: '[data-ref="addToCartPrice"]',
    option: 'product-button-option',
    item: '[data-ref="buttonItem"]',
    value: '.product-button-option__button-item-label__value',
    supply: '.product-button-option__button-item-label__supply',
    packPrice: '[data-ref="packPrice"]',
    soldOut: 'product-button-option__button-item--sold-out',
    noVariant: 'product-button-option__button-item--no-variant',
    selected: 'button--text-primary',
    camouflage: '.camouflage-marked-unavailable',
};

const initialised = new WeakSet();

// One live bar per section id, kept so the editor can unload or reload it.
const instances = new Map();

function destroy(sectionId) {
    const instance = instances.get(sectionId);
    if (!instance) return;
    instances.delete(sectionId);
    instance.teardown();
    instance.root.remove();
}

function init(root) {
    if (initialised.has(root)) return;
    initialised.add(root);

    // The theme's scroll fade-in puts a transform on section wrappers
    // (.animation-layer__out-of-view), and a transformed ancestor becomes the
    // containing block for position: fixed. Living directly under <body> keeps
    // the bar fixed to the viewport. appendChild moves the element, so every
    // listener and observer below binds to the moved node.
    const sectionId = root.dataset.sectionId;
    destroy(sectionId);
    document.body.appendChild(root);

    // Document, window and main-form listeners are removed through this on
    // unload; listeners on the bar's own elements go with the element.
    const controller = new AbortController();
    const signal = controller.signal;
    const cleanups = [() => controller.abort()];
    instances.set(sectionId, {
        root,
        teardown: () => cleanups.forEach((cleanup) => cleanup()),
    });

    const main = document.querySelector(MAIN.component);
    const form = main ? main.querySelector(MAIN.form) : null;
    const mainSubmit = form ? form.querySelector(MAIN.submit) : null;
    if (!main || !form || !mainSubmit) {
        root.hidden = true;
        return;
    }

    const mainStatus = mainSubmit.querySelector(MAIN.status);
    const mainPrice = form.querySelector(MAIN.price);
    const text = {
        addToBag: root.dataset.textAddToBag || '',
        pleaseSelectOptions: root.dataset.textPleaseSelectOptions || '',
        pleaseSelect: root.dataset.textPleaseSelect || '',
    };
    const button = root.querySelector('[data-zv-submit]');
    const buttonText = root.querySelector('[data-zv-button-text]');
    const buttonPrice = root.querySelector('[data-zv-price]');
    const dropdowns = Array.from(root.querySelectorAll('[data-zv-dropdown]')).map((el) => ({
        el,
        kind: el.dataset.zvDropdown,
        position: el.dataset.optionPosition,
        trigger: el.querySelector('[data-zv-trigger]'),
        value: el.querySelector('[data-zv-value]'),
        panel: el.querySelector('.zv-sticky-atc__panel'),
        items: [],
    }));

    let buttonState = 'add';
    let observedVisible = false;
    let forcedVisible = false;
    let renderQueued = false;

    // ----- Reading the main buy box --------------------------------------

    function mainOptionFor(dropdown) {
        return Array.from(main.querySelectorAll(MAIN.option)).find(
            (option) => option.getAttribute('position') === dropdown.position
        ) || null;
    }

    function readItems(dropdown) {
        const option = mainOptionFor(dropdown);
        if (!option) return [];
        return Array.from(option.querySelectorAll(MAIN.item)).map((item) => {
            const input = item.querySelector('input[type="radio"]');
            const label = item.querySelector('label');
            if (!input || !label) return null;
            // Camouflage hides a value after load without changing stock data, so
            // visibility is the only reliable signal for it.
            const hidden = item.offsetParent === null
                || item.querySelector(MAIN.camouflage) !== null
                || label.matches(MAIN.camouflage);
            if (hidden) return null;
            const valueEl = item.querySelector(MAIN.value);
            const supplyEl = item.querySelector(MAIN.supply);
            const priceEl = item.querySelector(MAIN.packPrice);
            return {
                item,
                value: input.value,
                // The main picker tracks selection with a class on the label, never
                // the radio's checked state (see component_ProductButtonOption.liquid).
                selected: label.classList.contains(MAIN.selected),
                disabled: input.disabled
                    || item.classList.contains(MAIN.soldOut)
                    || item.classList.contains(MAIN.noVariant),
                text: (valueEl ? valueEl.textContent : label.textContent).trim(),
                detail: supplyEl ? supplyEl.textContent.trim() : '',
                price: priceEl ? priceEl.textContent.trim() : '',
            };
        }).filter(Boolean);
    }

    // ----- Rendering -----------------------------------------------------

    function renderDropdown(dropdown) {
        dropdown.items = readItems(dropdown);
        const selected = dropdown.items.find((entry) => entry.selected);
        if (selected) {
            dropdown.value.textContent = selected.detail
                ? `${selected.text} | ${selected.detail}`
                : selected.text;
        } else {
            dropdown.value.textContent = text.pleaseSelect;
        }
        dropdown.trigger.disabled = dropdown.items.length === 0;

        const panel = dropdown.panel;
        panel.textContent = '';
        dropdown.items.forEach((entry, index) => {
            const row = document.createElement('li');
            row.className = 'zv-sticky-atc__option';
            row.id = `${panel.id}-option-${index}`;
            row.setAttribute('role', 'option');
            row.setAttribute('aria-selected', entry.selected ? 'true' : 'false');
            row.tabIndex = -1;
            row.dataset.index = String(index);
            if (entry.selected) row.classList.add('zv-sticky-atc__option--selected');
            if (entry.disabled) {
                row.classList.add('zv-sticky-atc__option--disabled');
                row.setAttribute('aria-disabled', 'true');
            }

            const labelEl = document.createElement('span');
            labelEl.className = 'zv-sticky-atc__option-label';
            const strong = document.createElement('strong');
            strong.textContent = entry.text;
            labelEl.appendChild(strong);
            if (entry.detail) labelEl.appendChild(document.createTextNode(` ${entry.detail}`));
            row.appendChild(labelEl);

            if (dropdown.kind === 'pack' && entry.price) {
                const priceEl = document.createElement('span');
                priceEl.className = 'zv-sticky-atc__option-price';
                priceEl.textContent = entry.price;
                row.appendChild(priceEl);
            }
            panel.appendChild(row);
        });
    }

    function renderButton() {
        const classes = mainSubmit.classList;
        if (classes.contains('is-select_option') || classes.contains('is-disabled')) {
            buttonState = 'select';
            buttonText.textContent = text.pleaseSelectOptions;
        } else if (classes.contains('is-sold_out') || classes.contains('is-unavailable')) {
            buttonState = 'soldout';
            buttonText.textContent = mainStatus ? mainStatus.textContent.trim() : '';
        } else {
            buttonState = 'add';
            buttonText.textContent = text.addToBag;
        }
        const disabled = buttonState !== 'add';
        button.classList.toggle('zv-sticky-atc__button--disabled', disabled);
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        if (buttonPrice) {
            const price = mainPrice ? mainPrice.textContent.trim() : '';
            buttonPrice.textContent = price;
            buttonPrice.hidden = disabled || price === '';
        }
    }

    function render() {
        dropdowns.forEach(renderDropdown);
        renderButton();
    }

    function queueRender() {
        if (renderQueued) return;
        renderQueued = true;
        window.requestAnimationFrame(() => {
            renderQueued = false;
            render();
        });
    }

    // ----- Dropdown open / close -----------------------------------------

    function isOpen(dropdown) {
        return dropdown.el.classList.contains(OPEN_CLASS);
    }

    function close(dropdown, focusTrigger) {
        if (!isOpen(dropdown)) return;
        dropdown.el.classList.remove(OPEN_CLASS);
        dropdown.trigger.setAttribute('aria-expanded', 'false');
        dropdown.panel.hidden = true;
        if (focusTrigger) dropdown.trigger.focus();
    }

    function closeAll(except) {
        dropdowns.forEach((dropdown) => {
            if (dropdown !== except) close(dropdown, false);
        });
    }

    function focusOption(dropdown, index) {
        const rows = dropdown.panel.querySelectorAll('[role="option"]');
        if (!rows.length) return;
        const next = Math.max(0, Math.min(rows.length - 1, index));
        rows[next].focus();
        dropdown.panel.setAttribute('aria-activedescendant', rows[next].id);
    }

    function open(dropdown, focusWhere) {
        closeAll(dropdown);
        renderDropdown(dropdown);
        if (!dropdown.items.length) return;
        dropdown.el.classList.add(OPEN_CLASS);
        dropdown.trigger.setAttribute('aria-expanded', 'true');
        dropdown.panel.hidden = false;
        if (focusWhere !== undefined) {
            const selectedIndex = dropdown.items.findIndex((entry) => entry.selected);
            let index = focusWhere;
            if (focusWhere === 'selected') index = selectedIndex >= 0 ? selectedIndex : 0;
            if (focusWhere === 'last') index = dropdown.items.length - 1;
            focusOption(dropdown, index);
        }
    }

    function choose(dropdown, index) {
        const entry = dropdown.items[index];
        if (!entry || entry.disabled) return;
        close(dropdown, true);
        // The main option's own click listener checks the radio and dispatches
        // the change event that drives the main form.
        entry.item.click();
        queueRender();
    }

    dropdowns.forEach((dropdown) => {
        dropdown.trigger.addEventListener('click', () => {
            if (isOpen(dropdown)) close(dropdown, false);
            else open(dropdown);
        });

        dropdown.trigger.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault();
                open(dropdown, 'selected');
            } else if (event.key === 'Escape' && isOpen(dropdown)) {
                event.preventDefault();
                close(dropdown, true);
            }
        });

        dropdown.panel.addEventListener('click', (event) => {
            const row = event.target.closest('[role="option"]');
            if (row) choose(dropdown, Number(row.dataset.index));
        });

        dropdown.panel.addEventListener('keydown', (event) => {
            const row = event.target.closest('[role="option"]');
            const index = row ? Number(row.dataset.index) : 0;
            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    focusOption(dropdown, index - 1);
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    focusOption(dropdown, index + 1);
                    break;
                case 'Home':
                    event.preventDefault();
                    focusOption(dropdown, 0);
                    break;
                case 'End':
                    event.preventDefault();
                    focusOption(dropdown, dropdown.items.length - 1);
                    break;
                case 'Enter':
                case ' ':
                    event.preventDefault();
                    if (row) choose(dropdown, index);
                    break;
                case 'Escape':
                    event.preventDefault();
                    close(dropdown, true);
                    break;
                case 'Tab':
                    close(dropdown, false);
                    break;
                default:
                    break;
            }
        });
    });

    document.addEventListener('pointerdown', (event) => {
        if (!root.contains(event.target)) closeAll();
    }, { signal });

    // ----- Button --------------------------------------------------------

    button.addEventListener('click', () => {
        closeAll();
        if (buttonState === 'select') {
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (buttonState !== 'add') return;
        // The main form's submit listener is the single add to cart path
        // (ProductForm.listenForFormSubmission). requestSubmit fires it once.
        if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
        } else {
            mainSubmit.click();
        }
    });

    // ----- Keeping in sync with the main form ----------------------------

    form.addEventListener('change', queueRender, { signal });

    const mutations = new MutationObserver(queueRender);
    cleanups.push(() => mutations.disconnect());
    mutations.observe(mainSubmit, { attributes: true, attributeFilter: ['class'] });
    if (mainPrice) {
        mutations.observe(mainPrice, { childList: true, characterData: true, subtree: true });
    }

    // The main component also exposes a variant-change event once its module
    // has upgraded the element. Subscribe when it is there; the listeners above
    // already cover every visible change, so nothing depends on it.
    try {
        const event = main.onSelectedPropertyChange;
        if (event && typeof event.subscribe === 'function') {
            event.subscribe(queueRender);
            cleanups.push(() => {
                if (typeof event.unsubscribe === 'function') event.unsubscribe(queueRender);
            });
        }
    } catch (error) {
        // The element has not been upgraded yet. The DOM listeners cover it.
    }

    // ----- Visibility ----------------------------------------------------

    function applyVisibility() {
        const visible = MOBILE.matches && (forcedVisible || observedVisible);
        root.classList.toggle(VISIBLE_CLASS, visible);
        document.documentElement.classList.toggle(PAGE_CLASS, visible);
        if (!visible) closeAll();
    }

    // The selection range runs from the first Pack Size / Packaging option
    // group in the main form down to the main add to cart actions. The whole
    // form is too wide a target: it also holds the instalments, delivery
    // cutoff, payment icons and trust bar below the button.
    const mainActions = form.querySelector('.product-form__actions');

    function rangeStart() {
        const groups = dropdowns
            .map((dropdown) => mainOptionFor(dropdown))
            .filter(Boolean);
        if (!groups.length) return mainActions;
        // Whichever group comes first in the DOM (Packaging is ordered above
        // Pack Size on the live PDP).
        return groups.reduce((first, group) => (
            first.compareDocumentPosition(group) & Node.DOCUMENT_POSITION_PRECEDING ? group : first
        ));
    }

    let rangeStartEl = null;

    function evaluate() {
        const start = rangeStartEl;
        const end = mainActions;
        if (!start || !end) {
            observedVisible = true;
        } else {
            const top = start.getBoundingClientRect().top;
            const bottom = end.getBoundingClientRect().bottom;
            // Hidden while any part of the selection is on screen, in either
            // scroll direction; visible above and below it.
            observedVisible = !(top < window.innerHeight && bottom > 0);
        }
        applyVisibility();
    }

    let evaluateQueued = false;
    function queueEvaluate() {
        if (evaluateQueued) return;
        evaluateQueued = true;
        window.requestAnimationFrame(() => {
            evaluateQueued = false;
            evaluate();
        });
    }

    // The two ends are watched as triggers; the scroll and resize listeners
    // catch the range's middle crossing the viewport, where neither end does.
    let intersection = null;
    function startWatching() {
        if (intersection !== null) return;
        rangeStartEl = rangeStart();
        if ('IntersectionObserver' in window) {
            intersection = new IntersectionObserver(queueEvaluate, { threshold: 0 });
            if (rangeStartEl) intersection.observe(rangeStartEl);
            if (mainActions && mainActions !== rangeStartEl) intersection.observe(mainActions);
        } else {
            intersection = false;
        }
        window.addEventListener('scroll', queueEvaluate, { passive: true });
        window.addEventListener('resize', queueEvaluate, { passive: true });
        evaluate();
    }

    function stopWatching() {
        if (intersection === null) return;
        if (intersection) intersection.disconnect();
        intersection = null;
        window.removeEventListener('scroll', queueEvaluate);
        window.removeEventListener('resize', queueEvaluate);
        observedVisible = false;
    }

    function handleBreakpoint() {
        if (MOBILE.matches) {
            startWatching();
        } else {
            stopWatching();
        }
        applyVisibility();
    }

    if (typeof MOBILE.addEventListener === 'function') {
        MOBILE.addEventListener('change', handleBreakpoint, { signal });
    } else {
        MOBILE.addListener(handleBreakpoint);
        cleanups.push(() => MOBILE.removeListener(handleBreakpoint));
    }

    // Stops the range watching and the scroll / resize listeners, and gives the
    // page back its padding.
    cleanups.push(() => {
        stopWatching();
        document.documentElement.classList.remove(PAGE_CLASS);
    });

    // Page padding so the footer and last content clear the bar.
    if ('ResizeObserver' in window) {
        const resize = new ResizeObserver(() => {
            document.documentElement.style.setProperty('--zv-sticky-atc-height', `${root.offsetHeight}px`);
        });
        resize.observe(root);
        cleanups.push(() => resize.disconnect());
    } else {
        document.documentElement.style.setProperty('--zv-sticky-atc-height', `${root.offsetHeight}px`);
    }

    // Theme editor: keep the bar on screen while this section is selected so
    // it can be previewed without scrolling.
    if (root.dataset.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
            if (event.detail && event.detail.sectionId === sectionId) {
                forcedVisible = true;
                applyVisibility();
            }
        }, { signal });
        document.addEventListener('shopify:section:deselect', (event) => {
            if (event.detail && event.detail.sectionId === sectionId) {
                forcedVisible = false;
                applyVisibility();
            }
        }, { signal });
    }

    render();
    handleBreakpoint();
}

function initAll() {
    document.querySelectorAll('[data-zv-sticky-atc]').forEach(init);
}

initAll();

// The editor re-renders the section's HTML without re-running this module.
// The fresh copy arrives inside the section wrapper while the old one still
// sits under <body>, so the old one is removed first (init does this through
// destroy) and only then the new one is moved.
document.addEventListener('shopify:section:load', (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    if (sectionId) destroy(sectionId);
    initAll();
});

document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    if (sectionId) destroy(sectionId);
});
